import React, { useCallback, useEffect, useRef } from "react";
import "./App.css";
import { Routes, Route, Outlet, useNavigate, useLocation } from "react-router";
import { Modal, ModalRef } from "./components/modal/modal";
import { Home } from "./pages/Home";
import LandingRouter from "./pages/LandingRouter";
import ScrollToTopButton from "./components/scrollToTop/ScrollToTop";
import { AuthRoute } from "./shared/auth/AuthRoute";
import { Role } from "./shared/enums/Role";
import axios from "axios";
import { ToasterComponent } from "./components/toaster/toaster";
import ExternalApplicationEvidence from "./pages/external/ExternalApplicationEvidence";
import ExternalApplicationRequest from "./pages/external/ExternalApplicationRequest";
import ApplicationsList from "./pages/appllicationPages/ApplicationsList";
import ApplicationView from "./pages/appllicationPages/ApplicationView";
import { Footer } from "./components/footer/Footer";
import { LoginPage } from "./pages/Login";
import LogoutPage from "./pages/Logout";
import ApplicationFilesPage from "./pages/appllicationPages/ApplicationFilesPage";
import ChildPage from "./pages/appllicationPages/ChildPage";
import KindergartenPage from "./pages/appllicationPages/KindergartenPage";
import ApplicationTypePage from "./pages/appllicationPages/ApplicationTypePage";
import { SpinnerComponent } from "./components/spinner/SpinnerComponent";
import AdminNotesPage from "./pages/appllicationPages/AdministrativeNotesPage";
import AssignAssessorPage from "./pages/appllicationPages/AssignAssessorPage";
import { ApplicationContextProvider } from "./components/applicationComponents/context/applicationContext";
import MyApplicationsList from "./pages/appllicationPages/MyApplicationsList";
import ManageUsers from "./pages/useManagment/ManageUsers";
import EditUser from "./pages/useManagment/EditUser";
import { useAccount, useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { BrowserAuthError, CacheLookupPolicy, InteractionRequiredAuthError } from "@azure/msal-browser";
import AssignStatusPage from "./pages/appllicationPages/AssignStatusPage";
import ApplicationChecklistPage from "./pages/appllicationPages/ApplicationChecklistPage";

function App() {
  axios.defaults.headers.post["Content-Type"] = "application/json";
  const modalRef = useRef<ModalRef>(null);
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const location = useLocation();
  const ignoreRoutes = ["/", "/externalRequest", "/login", "/logout", "/applicationevidence"]; //don't force a redirect to the /logout page if on these pages.

  useEffect(() => {
    if (ignoreRoutes.filter((x) => x == location.pathname).length == 0) {
      isAuthenticated();
    }
  });

  const isAuthenticated = () => {
    if (account?.idTokenClaims?.exp && account?.idTokenClaims?.exp * 1000 < Date.now()) {
      getAccessToken();
    }

    if (!account) {
      navigate("/logout", { relative: "path" });
    }
  };

  const getAccessToken = useCallback(async () => {
    if (!account) {
      throw new Error("No account available");
    }
    const accessTokenRequest = {
      scopes: loginRequest.scopes,
      account: account,
      cacheLookupPolicy: CacheLookupPolicy.Default,
    };

    return await instance.acquireTokenSilent(accessTokenRequest).catch(async (error) => {
      if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
        try {
          if (account && (inProgress === "none" || inProgress === "startup")) {
            const tokenResponse = await instance.acquireTokenPopup({
              scopes: loginRequest.scopes,
            });
            return tokenResponse.accessToken;
          }
        } catch (popupError) {
          // Fallback to redirect if popup fails
          instance.acquireTokenRedirect({
            scopes: loginRequest.scopes,
          });
        }
      }
    });
  }, [instance, account, inProgress]);

  return (
    <>
      <Modal cancelAction={() => console.log("closing")} confirmAction={() => console.log("confirm")} ref={modalRef} />
      <div className="app-container">
        <Routes>
          <Route>
            <Route path="/*" element={<LandingRouter />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="logout" element={<LogoutPage />} />
            <Route
              path="home"
              element={
                <AuthRoute roles={[Role.assessor, Role.manager, Role.root]}>
                  <Home />
                </AuthRoute>
              }
            />
            <Route
              path="applications"
              element={
                <AuthRoute roles={[Role.assessor, Role.manager, Role.root]}>
                  <Outlet />
                </AuthRoute>
              }>
              <Route index element={<ApplicationsList />} />
              <Route
                path=":id"
                element={
                  <ApplicationContextProvider>
                    <Outlet />
                  </ApplicationContextProvider>
                }>
                {applicationChildren}
              </Route>
            </Route>
            <Route
              path="myapplications"
              element={
                <AuthRoute roles={[Role.assessor, Role.manager, Role.root]}>
                  <Outlet />
                </AuthRoute>
              }>
              <Route index element={<MyApplicationsList />} />
              <Route
                path=":id"
                element={
                  <ApplicationContextProvider>
                    <Outlet />
                  </ApplicationContextProvider>
                }>
                {applicationChildren}
              </Route>
            </Route>
            <Route
              path="manageUsers"
              element={
                <AuthRoute roles={[Role.manager, Role.root]}>
                  <Outlet />
                </AuthRoute>
              }>
              <Route index element={<ManageUsers />} />
              <Route path="new" element={<EditUser />} />
              <Route path=":id" element={<EditUser />} />
            </Route>
            <Route path="applicationevidence" element={<ExternalApplicationEvidence />} />
            <Route
              path="externalRequest"
              element={
                <>
                  <ExternalApplicationRequest />
                </>
              }
            />
          </Route>
        </Routes>
        <Footer />
        <ToasterComponent />
        <SpinnerComponent />
      </div>
      <ScrollToTopButton />
    </>
  );
}

const applicationChildren: React.ReactElement[] = [
  <Route key="1" index element={<ApplicationView />} />,
  <Route key="2" path="applicationfiles" element={<ApplicationFilesPage />} />,
  <Route key="3" path="child" element={<ChildPage />} />,
  <Route key="4" path="assignstatus" element={<AssignStatusPage />} />,
  <Route key="5" path="assignassessor" element={<AssignAssessorPage />} />,
  <Route key="6" path="applicationtype" element={<ApplicationTypePage />} />,
  <Route key="7" path="kindergarten" element={<KindergartenPage />} />,
  <Route key="8" path="adminNotes" element={<AdminNotesPage />} />,
  <Route key="9" path="applicationchecklist" element={<ApplicationChecklistPage />} />,
];

export default App;
